/**
 * ClientLinksCard (KAN-666): "Связанные карточки" on the client page.
 *
 * Lists the links of the client card (KAN-642): the linked card's name as a
 * link to /clients/{id}, its role and company, an "авто" badge for a link
 * confirmed automatically, and one "Отменить связь" button (KAN-667: links
 * are confirmed on creation, nothing waits for a decision). The list and the
 * cancel come from useClientLinkList, shared with the transcript modal header,
 * so a cancel here refreshes the links there as well.
 */
import { Link } from 'react-router-dom';
import { Link2, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { ClientLink } from '@/types/client';
import {
  isAutoConfirmed,
  linkedClientDetails,
  linkedClientName,
  useClientLinkList,
} from './useClientLinkList';

interface ClientLinksCardProps {
  clientId: string;
}

function LinkRow({
  link,
  cancelling,
  onCancel,
}: {
  link: ClientLink;
  cancelling: boolean;
  /** Not passed: no cancel button (KAN-811: MANAGER role). */
  onCancel?: () => void;
}): JSX.Element {
  const { client } = link;
  const details = linkedClientDetails(client);
  return (
    <li
      className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1"
      data-testid="client-link-row"
    >
      <span className="inline-flex flex-wrap items-center gap-x-2 text-sm text-gray-700">
        <span>
          <Link
            to={`/clients/${client.id}`}
            className="font-medium text-blue-700 hover:underline"
          >
            {linkedClientName(client)}
          </Link>
          {details && <span data-testid="client-link-details">{details}</span>}
        </span>
        {isAutoConfirmed(link) && (
          <Badge variant="secondary" title="Связана автоматически">
            авто
          </Badge>
        )}
      </span>
      {onCancel && (
        <span className="inline-flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-gray-600"
            disabled={cancelling}
            onClick={onCancel}
          >
            Отменить связь
          </Button>
          {cancelling && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
        </span>
      )}
    </li>
  );
}

export function ClientLinksCard({ clientId }: ClientLinksCardProps): JSX.Element {
  const { links, loading, error, decide, decidingLinkId, canDecide } =
    useClientLinkList(clientId);

  let body: JSX.Element;
  if (loading) {
    body = (
      <div className="flex items-center justify-center py-6">
        <Loader2
          className="h-6 w-6 animate-spin text-gray-400"
          data-testid="client-links-loading"
        />
      </div>
    );
  } else if (error) {
    body = (
      <p className="text-center py-4 text-sm text-red-600">
        Не удалось загрузить связи
      </p>
    );
  } else if (links.length === 0) {
    body = <p className="text-center py-4 text-sm text-gray-500">Связей нет</p>;
  } else {
    body = (
      <ul className="space-y-3" data-testid="client-links-list">
        {links.map((link) => (
          <LinkRow
            key={link.id}
            link={link}
            cancelling={decidingLinkId === link.id}
            onCancel={canDecide ? () => decide(link.id, 'reject') : undefined}
          />
        ))}
      </ul>
    );
  }

  return (
    <Card data-testid="client-links-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          Связанные карточки
        </CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
